/**
 * MM VTO Manager - Moon Moon Agency
 * Intégration Fittingbox Virtual Try-On Advanced sur la PDP.
 *
 * - Lit la config injectée par Liquid via [data-vto-config] (apiKey, uid initial, variants)
 * - Vérifie via batch availability quelles variantes (EAN) sont viewerCompatible
 * - Construit le carousel natif Fittingbox avec ces variantes (sélecteur couleur in-popup)
 * - Popup plein écran centré (centeredHorizontal + centeredVertical + 100vw/100vh)
 * - À chaque changement de variant Shopify : setFrame(newBarcode) sans recréer l'instance
 *
 * DEBUG : window.MMVtoManager.state pour inspecter l'état complet.
 *         Logs détaillés via window.log (visibles en dev / sur myshopify.com).
 *
 * Cf. .claude/rules/javascript.md (Manager pattern).
 */

const LOG = '👓 [VTO]';

const MMVtoManager = {
	config: {
		selectors: {
			wrapper:      '[data-mm-main-product]',
			cfg:          '[data-vto-config]',
			trigger:      '[data-vto-trigger]',     // mode live (AR webcam)
			trigger3d:    '[data-vto-3d-trigger]',  // mode viewer (rotation 3D)
			container:    '[data-vto-container]',
			variantsData: '[data-variants-data]',
		},
		api: {
			availability: 'https://product-api.fittingbox.com/glasses-metadata/availability',
		},
	},

	state: {
		apiKey: null,
		lang: 'fr',
		currentUid: null,
		productTitle: '',
		productImage: '',
		variants: [],            // [{label, barcode, thumbnailUrl}]
		availabilityResults: [], // raw API response : [{uid, available, viewerCompatible, annotated}]
		availableUids: [],       // existe chez Fittingbox → mode live ok
		viewerCompatibleUids: [],// rotation 3D dispo  → mode viewer ok
		instance: null,
		ready: false,
		availabilityChecked: false,
		lastIssue: null,         // dernier issue Fittingbox (debug)
	},

	init() {
		const wrapper = document.querySelector(this.config.selectors.wrapper);
		if (!wrapper) return;

		const cfgEl = wrapper.querySelector(this.config.selectors.cfg);
		if (!cfgEl) return;

		let cfg;
		try { cfg = JSON.parse(cfgEl.textContent || '{}'); }
		catch (e) { window.log('error', LOG, 'config JSON parse failed', { error: e.message }); return; }

		if (!cfg.apiKey) { window.log('warn', LOG, 'no apiKey in config — VTO disabled'); return; }

		this.state.apiKey       = cfg.apiKey;
		this.state.lang         = cfg.lang || 'fr';
		this.state.currentUid   = cfg.uid || null;
		this.state.productTitle = cfg.productTitle || '';
		this.state.productImage = cfg.productImage || '';
		this.state.variants     = Array.isArray(cfg.variants) ? cfg.variants : [];

		window.log('info', LOG, 'init', {
			currentUid: this.state.currentUid,
			variantsCount: this.state.variants.length,
			variants: this.state.variants,
		});

		/* Délégation : survit aux replacements de DOM via product-reload */
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.trigger))   this._open('live');
			if (e.target.closest(this.config.selectors.trigger3d)) this._open('viewer');
		});

		document.addEventListener('product:variant-found', (e) => this._onVariantChange(e));

		this._checkAllVariants().then(() => this._refreshTriggers());
	},

	_getTriggerLive() {
		const wrapper = document.querySelector(this.config.selectors.wrapper);
		return wrapper?.querySelector(this.config.selectors.trigger) || null;
	},

	_getTrigger3d() {
		const wrapper = document.querySelector(this.config.selectors.wrapper);
		return wrapper?.querySelector(this.config.selectors.trigger3d) || null;
	},

	_onVariantChange(e) {
		const variantId = e?.detail?.data?.variantId;
		if (!variantId) return;

		const variant = this._findVariantById(variantId);
		if (!variant) { window.log('warn', LOG, 'variant not found in DOM', { variantId }); return; }

		const uid = variant.barcode || null;
		window.log('info', LOG, 'variant change', { variantId, sku: variant.sku, barcode: uid });

		if (!uid) { this._refreshTriggers(); return; }

		this.state.currentUid = uid;
		this._refreshTriggers();

		/* setFrame uniquement si le modele est available (sinon Fittingbox echoue) */
		if (this.state.availableUids.includes(uid) && this.state.ready && this.state.instance) {
			try { this.state.instance.setFrame(uid); window.log('info', LOG, 'setFrame', { uid }); }
			catch (err) { window.log('error', LOG, 'setFrame failed', { uid, error: err.message }); }
		}
	},

	_findVariantById(variantId) {
		const wrapper = document.querySelector(this.config.selectors.wrapper);
		const dataEl = wrapper?.querySelector(this.config.selectors.variantsData);
		if (!dataEl) return null;
		try {
			const list = JSON.parse(dataEl.textContent || '[]');
			return list.find(v => v.id == variantId) || null;
		} catch (e) { window.log('error', LOG, 'variants data parse failed', { error: e.message }); return null; }
	},

	_toggleTrigger(el, show) {
		if (!el) return;
		if (show) { el.hidden = false; el.removeAttribute('hidden'); }
		else { el.hidden = true; }
	},

	/* Affichage des boutons : on les montre des qu'AU MOINS UNE variante du produit
	 * est compatible (mode live ou 3D viewer). Si l'utilisateur clique avec une
	 * variante non compatible selectionnee, _open() fallback sur la 1re variante
	 * compatible — le selecteur de variante INTEGRE au popup Fittingbox permet
	 * ensuite de changer (cf. brief Celine 2026-08).
	 *  - "Essayage virtuel" (live AR)       : >= 1 variante available
	 *  - "Voir en 3D"      (viewer rotation): >= 1 variante viewerCompatible */
	_refreshTriggers() {
		const hasLive = this.state.availableUids.length > 0;
		const hasV3d  = this.state.viewerCompatibleUids.length > 0;
		this._toggleTrigger(this._getTriggerLive(), hasLive);
		this._toggleTrigger(this._getTrigger3d(), hasV3d);
	},

	async _checkAllVariants() {
		if (this.state.availabilityChecked) return;
		this.state.availabilityChecked = true;

		const allUids = this.state.variants.map(v => v.barcode).filter(Boolean);
		if (this.state.currentUid && !allUids.includes(this.state.currentUid)) {
			allUids.push(this.state.currentUid);
		}
		if (!allUids.length) { window.log('warn', LOG, 'no barcodes to check'); return; }

		try {
			const url = `${this.config.api.availability}?apiKey=${encodeURIComponent(this.state.apiKey)}&uidList=${encodeURIComponent(allUids.join(','))}`;
			const res = await fetch(url);
			if (!res.ok) { window.log('error', LOG, 'availability HTTP error', { status: res.status }); return; }
			const arr = await res.json();
			this.state.availabilityResults = Array.isArray(arr) ? arr : [];
			this.state.availableUids = this.state.availabilityResults
				.filter(x => x && x.available)
				.map(x => x.uid);
			this.state.viewerCompatibleUids = this.state.availabilityResults
				.filter(x => x && x.viewerCompatible)
				.map(x => x.uid);

			window.log('info', LOG, 'availability results', {
				checked: allUids.length,
				available: this.state.availableUids.length,
				viewerCompatible: this.state.viewerCompatibleUids.length,
				details: this.state.availabilityResults,
			});

			/* Create instance des qu'on a >= 1 variante available — meme si la variante
			 * courante n'est pas compatible. Le widget Fittingbox s'init avec la 1re
			 * variante compatible (cf. _createInstance qui choisit initFrame), et le
			 * fallback dans _open() bascule sur une variante valide au clic. */
			if (this.state.availableUids.length) {
				this._createInstance();
			}
		} catch (err) {
			window.log('error', LOG, 'availability fetch failed', { error: err.message });
		}
	},

	_buildCarousel() {
		/* On inclut TOUTES les variantes available (le live AR fonctionne dès available:true) */
		const compatibles = this.state.variants.filter(v =>
			v.barcode && this.state.availableUids.includes(v.barcode)
		);
		if (!compatibles.length) return null;

		const variantsBlock = compatibles.map(v => ({
			variantLabel: v.label || '',
			variantThumbnailUrl: v.thumbnailUrl || '',
			sizes: [{ sizeLabel: '', frameId: v.barcode }],
		}));

		return {
			frames: [{
				frameLabel: this.state.productTitle || '',
				frameImageUrl: this.state.productImage || '',
				variants: variantsBlock,
			}],
		};
	},

	_createInstance() {
		if (this.state.instance) return;
		if (typeof FitMix === 'undefined') {
			window.log('info', LOG, 'FitMix not loaded yet, retry at window load');
			window.addEventListener('load', () => this._createInstance(), { once: true });
			return;
		}
		const carousel = this._buildCarousel();
		/* Frame d'init : si la variante courante est compatible on l'utilise, sinon
		 * on prend la 1re variante available (sinon le widget echoue a l'init). */
		const initFrame = this.state.availableUids.includes(this.state.currentUid)
			? this.state.currentUid
			: this.state.availableUids[0];
		const params = {
			apiKey: this.state.apiKey,
			frame: initFrame,
			lang: this.state.lang,
			/* Popup centrée (mobile : auto-fullscreen par Fittingbox). Le ratio paysage
			 * fait passer le carousel en bas horizontal au lieu de sidebar droite.
			 * height ajustee : 600px etait trop court pour l'ecran d'accueil Fittingbox
			 * (CTA "utiliser ce service" en bas debordait, pas de scroll interne).
			 * width elargie pour eviter un split vertical trop compresse sur l'accueil. */
			popupIntegration: {
				centeredHorizontal: true,
				centeredVertical: true,
				width: 'min(95vw, 1000px)',
				height: 'min(95vh, 780px)',
			},
			onIssue: (issue) => {
				this.state.lastIssue = issue;
				window.log('error', LOG, 'Fittingbox onIssue', issue);
			},
			onMode: (mode) => window.log('info', LOG, 'onMode', { mode }),
			onOpenStream: () => window.log('info', LOG, 'onOpenStream (camera ok)'),
			onStopVto: () => window.log('info', LOG, 'onStopVto'),
		};
		if (carousel) params.carousel = carousel;

		window.log('info', LOG, 'createWidget', { frame: params.frame, carouselSize: carousel?.frames?.[0]?.variants?.length || 0 });

		try {
			this.state.instance = FitMix.createWidget(
				'mm-vto-container',
				params,
				() => {
					this.state.ready = true;
					window.log('info', LOG, 'widget ready');
					this._injectBranding();
				}
			);
		} catch (err) {
			window.log('error', LOG, 'createWidget failed', { error: err.message });
		}
	},

	/* Injecte le logo Céline Roland en haut de l'iframe VTO via addCss().
	 * Pas de paramètre Fittingbox officiel pour le logo → on utilise body::before. */
	_injectBranding() {
		if (!this.state.instance || typeof this.state.instance.addCss !== 'function') {
			window.log('warn', LOG, 'addCss not available, skipping branding');
			return;
		}
		const logoUrl = 'https://cdn.shopify.com/s/files/1/1002/4802/7462/files/logo-black.svg?v=1780382890';
		const css = `
			/* Header logo blanc full-width */
			body { background: #ffffff !important; }
			body::before {
				content: '';
				position: fixed;
				top: 0;
				left: 0;
				width: 100%;
				height: 60px;
				background: #ffffff url('${logoUrl}') center center / 180px auto no-repeat;
				border-bottom: 1px solid #e5e5e5;
				z-index: 99999;
				pointer-events: none;
			}

			/* Caméra plus haute → on rétrécit la zone catalog (mobile/petit popup) */
			div:has(> vto-advanced-glasses-catalog) {
				height: 220px !important;
				max-height: 220px !important;
			}

			/* Header "Couleur de monture" plus compact (Tailwind h-36 = 144px → 56px) */
			[data-testid="portait-tabs-component"] {
				height: 3.5rem !important;
			}

			/* Pastilles thumbnails agrandies (Tailwind size-6/8 = 24/32px → 56px) */
			vto-advanced-variant-color-button {
				width: 56px !important;
				height: 56px !important;
				min-width: 56px !important;
			}
		`;
		try { this.state.instance.addCss(css); window.log('info', LOG, 'branding injected'); }
		catch (err) { window.log('error', LOG, 'addCss failed', { error: err.message }); }
	},

	_open(mode) {
		/* Pour rester compatible avec le bouton "tjs visible" : si la variante courante
		 * n'est pas compatible avec le mode demande, on fallback sur la 1re variante
		 * compatible du produit. L'utilisateur peut ensuite changer via le carousel
		 * de variantes integre au popup Fittingbox. */
		const compatibleList = (mode === 'viewer')
			? this.state.viewerCompatibleUids
			: this.state.availableUids;
		let targetUid = this.state.currentUid;
		if (!compatibleList.includes(targetUid) && compatibleList.length) {
			targetUid = compatibleList[0];
			window.log('info', LOG, 'fallback to first compatible variant', { currentUid: this.state.currentUid, targetUid });
		}

		window.log('info', LOG, 'click → open', {
			mode: mode || 'live',
			currentUid: this.state.currentUid,
			targetUid,
			ready: this.state.ready,
			hasInstance: !!this.state.instance,
			available: this.state.availableUids.includes(targetUid),
			viewerCompatible: this.state.viewerCompatibleUids.includes(targetUid),
		});

		if (!this.state.instance) {
			window.log('warn', LOG, 'click but no instance yet — widget not created (Fittingbox SDK missing or availability check pending)');
			return;
		}
		try {
			if (targetUid && this.state.ready) {
				this.state.instance.setFrame(targetUid);
			}
			this.state.instance.startVto(mode || 'live');
		} catch (err) {
			window.log('error', LOG, 'startVto failed', { mode, error: err.message });
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => MMVtoManager.init());
} else {
	MMVtoManager.init();
}

window.MMVtoManager = MMVtoManager;
